// Per-run mutable state, centralized (v1 spread this across game3d.js fields;
// resetRunState() at js/game3d.js:70 is the reference for what resets per run).

import { DISTRICTS, type DistrictDef } from '@/content/districts';
import type { PowerUpId } from '@/content/powerups';

export interface RunState {
  score: number;
  distance: number;
  gameSpeed: number;
  combo: number;
  comboMultiplier: number;
  comboDecayTimer: number;
  powerUpScoreMultiplier: number;
  isInvincible: boolean;
  /** Second Wind charges left (1 default; 2 with Iron Spirit; 0 on some dailies) */
  shieldCharges: number;
  hitGraceTimer: number;
  nearMissCooldown: number;
  coinsCollected: number;
  district: DistrictDef;
  activePowerUpId: PowerUpId | null;
  powerUpTimer: number;
  magnetRadiusMultiplier: number;
  beltIndex: number;
  /** Phase 6 meta: owned gameplay modifiers + coin valuation + daily mode */
  modifiers: ReadonlySet<string>;
  coinValue: number;
  coinIntervalFactor: number;
  isDaily: boolean;
  dailyDate: string | null;
}

const FALLBACK = DISTRICTS['mitte'] as DistrictDef;

export interface RunOptions {
  modifiers?: ReadonlySet<string>;
  isDaily?: boolean;
  dailyDate?: string | null;
  shieldChargesOverride?: number | null;
  speedBonus?: number;
  coinValueFactor?: number;
  coinIntervalFactor?: number;
}

export function freshRunState(district: DistrictDef = FALLBACK, opts: RunOptions = {}): RunState {
  const modifiers = opts.modifiers ?? new Set<string>();
  const baseShield = modifiers.has('mod-double-shield') ? 2 : 1;
  const coinBase = modifiers.has('mod-coin-value') ? 12 : 10; // +20% (v1 coin = 10)
  return {
    score: 0,
    distance: 0,
    gameSpeed: district.baseSpeed + (opts.speedBonus ?? 0),
    combo: 0,
    comboMultiplier: 1.0,
    comboDecayTimer: 0,
    powerUpScoreMultiplier: 1.0,
    isInvincible: false,
    shieldCharges: opts.shieldChargesOverride ?? baseShield,
    hitGraceTimer: 0,
    nearMissCooldown: 0,
    coinsCollected: 0,
    district,
    activePowerUpId: null,
    powerUpTimer: 0,
    magnetRadiusMultiplier: 1.0,
    beltIndex: 0,
    modifiers,
    coinValue: Math.round(coinBase * (opts.coinValueFactor ?? 1)),
    coinIntervalFactor: opts.coinIntervalFactor ?? 1,
    isDaily: opts.isDaily ?? false,
    dailyDate: opts.dailyDate ?? null,
  };
}
